import React, { useEffect, useState } from "react";
import { Button, Card, Empty, Flex, Grid, Modal, Space } from "antd";
import { useTranslation } from "react-i18next";
import { useAntdTheme } from "../../shared/hooks";

interface BaseOrderColumnProps {
  orders: any[];
  renderOrder: (order: any) => React.ReactNode;
  renderOrderTitle?: (order: any) => React.ReactNode;
  renderOrderDetails: (order: any) => React.ReactNode;
  renderLeftButton?: (elemId: string, elemIndex: number) => React.ReactNode;
  renderRightButton?: (elem: any, elemIndex: number) => React.ReactNode;
  renderModalFooter?: (elem: any, defaultFooter: React.ReactNode) => React.ReactNode;
  emptyDescription: string;
  heading?: string;
  showHeading?: boolean;
  elemsMaxHeight?: string;
  modalCentered?: boolean;
  modalZIndex?: number;
  modalDestroyOnHidden?: boolean;
  useCustomModalStyles?: boolean;
}

/**
 * Base component for order columns that provides list rendering
 * and standardized modal behavior for order management.
 */
export const BaseOrderColumn: React.FC<BaseOrderColumnProps> = ({
  orders,
  renderOrder,
  renderOrderTitle,
  renderOrderDetails,
  renderLeftButton,
  renderRightButton,
  renderModalFooter,
  emptyDescription,
  heading,
  showHeading,
  elemsMaxHeight,
  modalCentered = false,
  modalZIndex = 1000,
  modalDestroyOnHidden = false,
  useCustomModalStyles = false,
}) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const screens = Grid.useBreakpoint();
  const [showOrderModal, setShowOrderModal] = useState<string | null>(null);

  // Responsive modal width following Ant Design patterns
  const getResponsiveModalWidth = () => {
    if (screens.xl) return 900;
    if (screens.lg) return 800;
    if (screens.md) return 700;
    // Mobile: use percentage width
    return "100%";
  };

  // Track order IDs to clear modal if order is removed
  const orderIDs = new Set<string>();
  orders.forEach((order) => {
    orderIDs.add(order.id);
  });

  useEffect(() => {
    setShowOrderModal((prev) => {
      if (prev && !orderIDs.has(prev)) {
        // Clear modal state if order is no longer in this column
        return null;
      }
      return prev;
    });
  }, [orders]);

  const getLeftButtonPadding = () => {
    if (screens.xl) return token.marginXL;
    if (screens.lg) return token.marginLG;
    if (screens.md) return token.marginMD;
    return token.marginSM;
  };

  const getOrderContentFontSize = () => {
    if (screens.xl) return "18px";
    if (screens.lg) return "16px";
    if (screens.md) return "14px";
    return "13px";
  };

  const getHeadingFontSize = () => {
    if (screens.xl) return "24px";
    if (screens.lg) return "22px";
    if (screens.md) return "20px";
    return "18px";
  };

  const getModalStyles = () => {
    if (!useCustomModalStyles) {
      return undefined;
    }

    // Responsive padding and height following Ant Design patterns - includes landscape phones
    const isMobile = !screens.lg;
    const bodyMaxHeight = isMobile ? "calc(100vh - 200px)" : "70vh";
    const horizontalPadding = isMobile ? token.paddingMD : token.paddingXL;
    const verticalPadding = isMobile ? token.paddingMD : token.paddingLG;

    return {
      header: {
        padding: `${verticalPadding}px ${horizontalPadding}px`,
        borderBottom: `1px solid ${token.colorBorder}`,
        marginBottom: 0,
        fontSize: getOrderContentFontSize(),
      },
      body: {
        padding: horizontalPadding,
        maxHeight: bodyMaxHeight,
        overflowY: "auto" as const,
        fontSize: getOrderContentFontSize(),
      },
      footer: {
        padding: `${verticalPadding}px ${horizontalPadding}px`,
        borderTop: `1px solid ${token.colorBorder}`,
        marginTop: 0,
      },
    };
  };

  return (
    <>
      {/* Heading */}
      {showHeading && (
        <div
          style={{
            marginBottom: token.marginMD,
            fontSize: getHeadingFontSize(),
            fontWeight: token.fontWeightStrong,
            color: token.colorText,
          }}
        >
          <span>
            {heading} <span style={{ color: token.colorTextSecondary }}>({orders.length})</span>
          </span>
        </div>
      )}

      {/* Order List */}
      {orders && orders.length > 0 ? (
        <Space
          direction="vertical"
          size="small"
          style={{
            width: "100%",
            ...(elemsMaxHeight && elemsMaxHeight !== "none"
              ? { maxHeight: elemsMaxHeight, overflowY: "auto" }
              : {}),
          }}
        >
          {orders.map((order, orderIndex) => {
            const onModalCancel = () => {
              setShowOrderModal(null);
            };

            // Responsive footer button sizing - includes landscape phones
            const isMobile = !screens.lg;
            const footerButtonSize = isMobile ? "middle" : "large";
            const footerButtonHeight = isMobile ? token.controlHeight : token.controlHeightLG;
            const footerButtonFontSize = isMobile ? "14px" : "16px";
            const footerButtonMinWidth = isMobile ? "100px" : "120px";

            const modalFooter = (
              <Button
                key="cancel"
                onClick={onModalCancel}
                size={footerButtonSize}
                block={isMobile}
                style={{
                  height: footerButtonHeight,
                  fontSize: footerButtonFontSize,
                  fontWeight: 600,
                  borderRadius: token.borderRadius,
                  minWidth: isMobile ? undefined : footerButtonMinWidth,
                }}
              >
                {t("cancel")}
              </Button>
            );

            const finalModalFooter = renderModalFooter
              ? renderModalFooter(order, modalFooter)
              : modalFooter;

            return (
              <Flex
                key={order.id}
                align="stretch"
                style={{
                  borderRadius: token.borderRadiusLG,
                  overflow: "hidden",
                  boxShadow: token.boxShadowSecondary,
                  border: `1px solid ${token.colorBorder}`,
                  background: token.colorBgContainer,
                }}
              >
                {/* Left Button (e.g., Star Priority) */}
                {renderLeftButton && (
                  <Flex
                    align="center"
                    justify="center"
                    style={{
                      paddingTop: 0,
                      paddingBottom: 0,
                      paddingLeft: getLeftButtonPadding(),
                      paddingRight: getLeftButtonPadding(),
                      cursor: "pointer",
                      borderRight: `1px solid ${token.colorBorder}`,
                      background: token.colorBgLayout,
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = token.colorFillSecondary;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = token.colorBgLayout;
                    }}
                  >
                    {renderLeftButton(order.id, orderIndex)}
                  </Flex>
                )}

                {/* Order Content */}
                <Flex
                  align="center"
                  flex={1}
                  onClick={() => {
                    setShowOrderModal(order.id);
                  }}
                  style={{
                    padding: token.marginMD,
                    cursor: "pointer",
                    minWidth: 0, // Allow flex shrinking
                    fontSize: getOrderContentFontSize(),
                  }}
                >
                  {renderOrder(order)}
                </Flex>

                {/* Modal */}
                <Modal
                  title={renderOrderTitle ? renderOrderTitle(order) : null}
                  open={showOrderModal === order.id}
                  destroyOnClose={modalDestroyOnHidden}
                  onCancel={onModalCancel}
                  footer={finalModalFooter}
                  width={getResponsiveModalWidth()}
                  centered={screens.md ? modalCentered : false}
                  zIndex={modalZIndex}
                  styles={getModalStyles()}
                  style={{
                    maxWidth: screens.md ? "900px" : "calc(100vw - 32px)",
                    top: screens.md ? undefined : 16,
                  }}
                >
                  {renderOrderDetails(order)}
                </Modal>

                {/* Right Button (e.g., Start/Done) */}
                {renderRightButton && (
                  <Flex align="stretch">{renderRightButton(order, orderIndex)}</Flex>
                )}
              </Flex>
            );
          })}
        </Space>
      ) : (
        <Card
          style={{
            borderRadius: token.borderRadiusLG,
            border: `1px solid ${token.colorBorder}`,
            boxShadow: token.boxShadowSecondary,
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: token.colorBgContainer,
          }}
        >
          <Empty description={emptyDescription} />
        </Card>
      )}
    </>
  );
};
